import type { AxiosInstance } from "axios";
import type { AssistantSessionSummary } from "@/components/codesmith/types";

export const ASSISTANT_SESSIONS_QUERY_KEY = "assistantSessions";
export const SESSIONS_PAGE_SIZE = 30;

/**
 * One pool of the sessions list response. Entries are lightweight
 * summaries; full session contents come from the per-session detail
 * endpoint. `non_migwiz_autofix_total` is `total` minus sessions that
 * exist only because the migration wizard enabled autofix on a
 * wizard-generated PR. The onboarding gate classifies fetched pool
 * sessions individually via their serialized `managed_prs` and uses this
 * aggregate to classify sessions beyond the fetched page (see
 * `onboardingRelevantSessionCount`).
 */
export type AssistantSessionsPoolResponse = {
  sessions: AssistantSessionSummary[];
  total: number;
  non_migwiz_autofix_total?: number;
};

/**
 * Build the org-scoping query fragment for the sessions list endpoint.
 *
 * Prefer the numeric `installation_id` when known. When it isn't (a superuser
 * viewing a foreign org that isn't in their installations list), fall back to
 * the explicit `org` name so the backend scopes to that org directly instead
 * of the persisted `active_org_name`, which the URL-sync POST may not have
 * updated yet.
 */
export function buildSessionsScopeParam(
  installationId?: number | null,
  orgName?: string | null,
): string {
  if (installationId) return `&installation_id=${installationId}`;
  if (orgName) return `&org=${encodeURIComponent(orgName)}`;
  return "";
}

export async function fetchAssistantSessions(
  api: AxiosInstance,
  installationId?: number | null,
  orgName?: string | null,
  options?: {
    limit?: number;
    /**
     * Drop top-level automation run sessions from the mine/team pools; the
     * sidebar shows them as grouped automation rows instead (see
     * useAutomationSessionGroups). Collaborating and archived pools always
     * keep them so those views stay exhaustive.
     */
    excludeAutomationSessions?: boolean;
  },
) {
  const limit = options?.limit ?? SESSIONS_PAGE_SIZE;
  const instParam = buildSessionsScopeParam(installationId, orgName);
  const excludeParam = options?.excludeAutomationSessions
    ? "&exclude_automation_sessions=1"
    : "";
  const [mineResponse, collaboratingResponse, teamResponse, archivedResponse] =
    await Promise.all([
      api.get(
        `/assistant/sessions?status=active&ownership=mine&limit=${limit}${instParam}${excludeParam}`,
      ),
      api.get(
        `/assistant/sessions?status=active&ownership=collaborating&limit=${limit}${instParam}`,
      ),
      api.get(
        `/assistant/sessions?status=active&ownership=team&limit=${limit}${instParam}${excludeParam}`,
      ),
      api.get(
        `/assistant/sessions?status=archived&ownership=mine&limit=${limit}${instParam}`,
      ),
    ]);
  return {
    mine: mineResponse.data as AssistantSessionsPoolResponse,
    collaborating: collaboratingResponse.data as AssistantSessionsPoolResponse,
    team: teamResponse.data as AssistantSessionsPoolResponse,
    archived: archivedResponse.data as AssistantSessionsPoolResponse,
  };
}
